/**
 * Canvas-rendered game timer display.
 *
 * Positioned in Row 0, centered horizontally between the menu and new game buttons.
 * Shows "M:SS" or "H:MM:SS" during gameplay and on the completion screen.
 */

import { appState } from './state'
import { timerGetElapsed, timerIsStarted, formatTime } from './timer'
import { isLightBackground } from './colorUtils'
import { rowY } from './allXYCoordinates'
import { CARD_FONT } from './canvasMethods'

/**
 * Draw the timer on the canvas.
 * Called from paint.ts during Playing and Completed states.
 */
export function drawTimer(ctx: CanvasRenderingContext2D): void {
    // Only show if the timer has been started (player made at least one move)
    if (!timerIsStarted()) return

    const elapsed = timerGetElapsed()
    const timeStr = formatTime(elapsed)

    const ch = appState.cardHeight
    const winW = appState.windowWidth
    const bgColor = appState.currentTheme.backgroundColor
    const lightBg = isLightBackground(bgColor)

    // Font size: proportional to card height, slightly smaller than button icons
    const fontSize = Math.floor(ch * 0.28)

    ctx.save()
    ctx.font = `500 ${fontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    // Color: subtle but readable against the background
    if (lightBg) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.45)'
    } else {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
    }

    // Position: centered in Row 0
    const y = rowY(0) + Math.floor(ch / 2)
    const x = Math.floor(winW / 2)

    ctx.fillText(timeStr, x, y)
    ctx.restore()
}

/**
 * Draw the timer on the completion screen (larger, more prominent)
 * with a "You Win!" subtitle below it.
 * @param overrideElapsed — If provided, use this value instead of the live timer.
 */
export function drawCompletionTimer(ctx: CanvasRenderingContext2D, overrideElapsed?: number): void {
    const elapsed = overrideElapsed ?? timerGetElapsed()
    const timeStr = formatTime(elapsed)

    const ch = appState.cardHeight
    const winW = appState.windowWidth
    const highlightColor = appState.currentTheme.highlightColor

    // Larger font for completion
    const fontSize = Math.floor(ch * 0.35)

    // Position: centered in Row 0
    const centerY = rowY(0) + Math.floor(ch / 2)
    const x = Math.floor(winW / 2)

    ctx.save()

    // Timer value
    ctx.font = `600 ${fontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
    ctx.fillText(timeStr, x, centerY - Math.floor(fontSize * 0.35))

    // "You Win!" subtitle in theme highlight color
    const subFontSize = Math.floor(ch * 0.2)
    ctx.font = `700 ${subFontSize}px ${CARD_FONT}`
    ctx.fillStyle = highlightColor
    ctx.fillText('You Win!', x, centerY + Math.floor(fontSize * 0.45))

    ctx.restore()
}
